import React, { useState } from 'react';
import CollectionSearch from '../search/CollectionSearch';
import { filterRecords } from '../../utils/search';
import {
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  Paper,
  Chip,
} from '@mui/material';
import { Lock as LockIcon } from '@mui/icons-material';

function CategoryLockSelector({
  categoriesData,
  lockedCategoryIds,
  setLockedCategoryIds,
}) {
  const [search, setSearch] = useState('');
  const handleToggleLock = (categoryId) => {
    if (lockedCategoryIds.includes(categoryId)) {
      setLockedCategoryIds(lockedCategoryIds.filter(id => id !== categoryId));
    } else {
      setLockedCategoryIds([...lockedCategoryIds, categoryId]);
    }
  };

  const handleLockAll = (accountId) => {
    const account = categoriesData.find(a => a.id === accountId);
    if (!account) return;

    const categoryIds = account.categories.map(c => c.id);
    const allLocked = categoryIds.every(id => lockedCategoryIds.includes(id));

    if (allLocked) {
      // Unlock all
      setLockedCategoryIds(lockedCategoryIds.filter(id => !categoryIds.includes(id)));
    } else {
      // Lock all
      const newLocked = [...new Set([...lockedCategoryIds, ...categoryIds])];
      setLockedCategoryIds(newLocked);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Calculate totals
  const allCategories = categoriesData.flatMap(a => a.categories);
  const visible = account => filterRecords(account.categories, search, category => [category.name, account.name], { rank: true });
  const shownCount = categoriesData.reduce((total, account) => total + visible(account).length, 0);
  const lockedTotal = allCategories
    .filter(c => lockedCategoryIds.includes(c.id))
    .reduce((sum, c) => sum + (c.monthly_allocation || 0), 0);
  const flexibleTotal = allCategories
    .filter(c => !lockedCategoryIds.includes(c.id))
    .reduce((sum, c) => sum + (c.monthly_allocation || 0), 0);

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Category Flexibility
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Select which categories should remain fixed in scenarios. Unlocked categories will be adjusted proportionally when income changes.
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <Chip
          icon={<LockIcon />}
          label={`Locked: ${formatCurrency(lockedTotal)}/mo`}
          color="default"
          variant="outlined"
        />
        <Chip
          label={`Flexible: ${formatCurrency(flexibleTotal)}/mo`}
          color="primary"
          variant="outlined"
        />
      </Box>

      <CollectionSearch label="Search category locks" value={search} onChange={setSearch}
        total={allCategories.length} shown={shownCount} />
      {search && <Typography variant="caption" display="block" sx={{ mb: 1 }}>Account checkboxes still apply to all categories in that account, including hidden categories.</Typography>}
      {search && !shownCount && <Typography>No matching categories.</Typography>}
      {categoriesData.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="text.secondary">
            No categories found. Categories are derived from your split templates.
          </Typography>
        </Paper>
      ) : (
        categoriesData.map(account => {
          if (search && !visible(account).length) return null;
          const categoryIds = account.categories.map(c => c.id);
          const allLocked = categoryIds.length > 0 && categoryIds.every(id => lockedCategoryIds.includes(id));
          const someLocked = categoryIds.some(id => lockedCategoryIds.includes(id));

          return (
            <Paper key={account.id} variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={allLocked}
                      indeterminate={someLocked && !allLocked}
                      onChange={() => handleLockAll(account.id)}
                    />
                  }
                  label={
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                      {account.name}
                    </Typography>
                  }
                />
                {allLocked && (
                  <LockIcon fontSize="small" color="action" sx={{ ml: 1 }} />
                )}
              </Box>

              <Box sx={{ pl: 4 }}>
                {account.categories.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No categories with allocations
                  </Typography>
                ) : (
                  visible(account).map(category => (
                    <Box
                      key={category.id}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        py: 0.5,
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={lockedCategoryIds.includes(category.id)}
                            onChange={() => handleToggleLock(category.id)}
                            size="small"
                          />
                        }
                        label={category.name}
                      />
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          {formatCurrency(category.monthly_allocation || 0)}/mo
                        </Typography>
                        {lockedCategoryIds.includes(category.id) && (
                          <LockIcon fontSize="small" color="action" />
                        )}
                      </Box>
                    </Box>
                  ))
                )}
              </Box>
            </Paper>
          );
        })
      )}

      <Paper sx={{ p: 2, mt: 3, bgcolor: 'info.light' }}>
        <Typography variant="body2">
          <strong>Tip:</strong> Lock categories that represent fixed expenses (rent, car payments, insurance)
          that won't change regardless of income changes. Leave flexible categories unlocked
          (savings, entertainment, dining) - these will adjust proportionally in your scenario.
        </Typography>
      </Paper>
    </Box>
  );
}

export default CategoryLockSelector;
